import { notifyIf } from '@baserow/modules/core/utils/error'
import {
  startPointerDrag,
  animateGhostTo,
} from '@baserow/modules/core/utils/dragAndDrop'

/**
 * This mixin can be used in combination with a view component that uses the
 * bufferedRows store mixin. It allows for drag and drop ordering of the items. It
 * can be used by adding the following listeners to the row objects.
 *
 * @pointerdown="rowDown($event, row)"
 * @mousemove="rowMoveOver($event, row)"
 * @mouseenter="rowMoveOver($event, row)"
 *
 * Note that the parent component must implement the `getDragAndDropStoreName` method.
 */
export default {
  data() {
    return {
      // Must be overwritten by the parent component. It should hold the class name
      // of the clone of the dom element when the user starts dragging the row.
      dragAndDropCloneClass: '',
      // Indicates whether a transition effect is currently active.
      dragAndDropTransitioning: false,
      // The row object that the user clicked on.
      dragAndDropDownRow: null,
      // Origem Flow: controlador do núcleo compartilhado de arrasto.
      dragAndDropController: null,
      // Cloned elements for drag visual feedback
      clonedElement: null,
      clonedWrapper: null,
      // Position offsets for dragged element
      dragAndDropDownRowTop: 0,
      dragAndDropDownRowLeft: 0,
    }
  },
  computed: {
    dragAndDropDraggingRow() {
      return this.$store.getters[
        `${this.getDragAndDropStoreName(this)}/getDraggingRow`
      ]
    },
  },
  methods: {
    /**
     * Should return the name of the store that's has the bufferedRows mixed in.
     * This can be different for each component, to this method must be overwritten.
     */
    getDragAndDropStoreName(props) {
      throw new Error(
        'The `getDragAndDropStoreName` method must be implemented.'
      )
    },
    /**
     * Origem Flow: pressionar o ponteiro num card (mouse, caneta ou toque).
     * O núcleo de arrasto engaja depois do threshold (ou long-press no
     * toque); soltar sem engajar dispara o rowClick (ex.: abrir o modal).
     */
    rowDown(event, row, readOnly = false) {
      // If it isn't a left click (touch has no buttons).
      if ((event.pointerType !== 'touch' && event.button !== 0) || !row) {
        return
      }

      if (event.pointerType !== 'touch') {
        event.preventDefault()
      }

      this.dragAndDropDownRow = row

      const cardElement = event.target
      const rect = cardElement.getBoundingClientRect()
      this.dragAndDropDownRowTop = event.clientY - rect.top
      this.dragAndDropDownRowLeft = event.clientX - rect.left

      this.dragAndDropController = startPointerDrag(event, {
        onStart: async (startEvent) => {
          // We only want to allow ordering by drag and drop if there aren't
          // any sortings because if there are, the ordering is not only based
          // of the `order` property and drag and dropping in the right
          // position doesn't make sense.
          if (readOnly || this.view.sortings.length > 0) {
            return
          }

          this.clonedElement = document.createElement('div')
          this.clonedElement.innerHTML = cardElement.outerHTML
          this.clonedElement.style = `position: absolute; left: 0; top: 0; width: ${rect.width}px; z-index: 10; pointer-events: none;`
          this.clonedElement.firstChild.classList.add(
            this.dragAndDropCloneClass
          )

          this.clonedWrapper = document.createElement('div')
          this.clonedWrapper.style =
            'position: fixed; left: 0; top: 0; right: 0; bottom: 0; overflow: hidden; pointer-events: none; z-index: 100;'
          this.clonedWrapper.appendChild(this.clonedElement)
          document.body.appendChild(this.clonedWrapper)

          this.rowMove(startEvent)
          await this.$store.dispatch(
            `${this.getDragAndDropStoreName(this)}/startRowDrag`,
            {
              row: this.dragAndDropDownRow,
            }
          )
        },
        onMove: (moveEvent) => this.rowMove(moveEvent),
        onUp: () => this.rowUp(),
        onCancel: () => {
          if (this.dragAndDropDraggingRow !== null) {
            this.$store.dispatch(
              `${this.getDragAndDropStoreName(this)}/cancelRowDrag`,
              {
                view: this.view,
                fields: this.fields,
                row: this.dragAndDropDraggingRow,
              }
            )
          }
          this.rowCancel()
        },
      })
    },
    /**
     * Called while the pointer moves after the drag has engaged. Follows the
     * pointer with the cloned element and, on touch, resolves the row under
     * the finger manually (touch events don't follow the finger because of
     * the implicit pointer capture).
     */
    rowMove(event) {
      if (this.clonedElement === null) {
        return
      }

      this.clonedElement.style.top =
        event.clientY - this.dragAndDropDownRowTop + 'px'
      this.clonedElement.style.left =
        event.clientX - this.dragAndDropDownRowLeft + 'px'

      // O alvo sob o ponteiro é resolvido via elementFromPoint para todos os
      // tipos de ponteiro: no toque os eventos não seguem o dedo (captura
      // implícita), e no mouse o preventDefault do pointerdown suprime os
      // mousemove de compatibilidade dos cards.
      if (this.dragAndDropDraggingRow !== null) {
        const el = document.elementFromPoint(event.clientX, event.clientY)
        const cardEl = el && el.closest('[data-row-id]')
        if (cardEl) {
          const row = this.$store.getters[
            `${this.getDragAndDropStoreName(this)}/getRow`
          ](parseInt(cardEl.dataset.rowId))
          if (row) {
            this.rowMoveOver({ target: cardEl }, row)
          }
        }
      }
    },
    /**
     * Called when the user releases the pointer after pressing down on a row. It
     * will check if we're in a dragging state and if so, we need to stop the
     * dragging of the row and make the new position persistent.
     */
    async rowUp() {
      if (this.dragAndDropDraggingRow !== null) {
        // Origem Flow: animação de pouso — o clone desliza até o slot onde o
        // card vai ficar antes da limpeza.
        const placeholder = document.querySelector(
          `.${this.dragAndDropCloneClass.replace('-clone', '')}`
        )
        const clone = this.clonedElement
        this.clonedElement = null
        if (clone && placeholder) {
          clone.firstChild.style.setProperty(
            'transition',
            'transform 150ms ease-out'
          )
          clone.firstChild.style.setProperty('transform', 'none', 'important')
          await animateGhostTo(clone, placeholder.getBoundingClientRect())
        }
        this.rowCancel()

        try {
          await this.$store.dispatch(
            `${this.getDragAndDropStoreName(this)}/stopRowDrag`,
            {
              table: this.table,
              view: this.view,
              fields: this.fields,
              primary: this.primary,
            }
          )
        } catch (error) {
          notifyIf(error)
        }
      } else {
        // Call the `rowClick` method because in some cases, we might want to take
        // certain action after a "normal" click on the row. Like for example
        // opening a row edit modal.
        this.rowClick(this.dragAndDropDownRow)
        this.rowCancel()
      }
    },
    rowCancel() {
      this.dragAndDropDownRow = null
      this.dragAndDropController?.dispose()
      // If the view is read only, the clonedWrapper is never created.
      if (this.clonedWrapper) {
        this.clonedWrapper.remove()
        this.clonedWrapper = null
        this.clonedElement = null
      }
    },
    /**
     * Must be called when the user hovers over another row. It will check if we're
     * currently in a dragging state and if so, the row is temporarily moved to the
     * new position.
     */
    async rowMoveOver(event, row) {
      if (
        row === null ||
        this.dragAndDropDraggingRow === null ||
        this.dragAndDropDraggingRow.id === row.id ||
        this.dragAndDropTransitioning
      ) {
        return
      }

      const moved = await this.$store.dispatch(
        `${this.getDragAndDropStoreName(this)}/forceMoveRowBefore`,
        {
          row: this.dragAndDropDraggingRow,
          targetRow: row,
        }
      )
      if (moved) {
        this.rowMoved()
      }
    },
    /**
     * After a row has been moved, we need to temporarily need to set the transition
     * state to true. While it's true, it can't be moved to another position to avoid
     * strange transition effects of other cards.
     */
    rowMoved() {
      this.dragAndDropTransitioning = true
      setTimeout(
        () => {
          this.dragAndDropTransitioning = false
        },
        // Must be kept in sync with the transition-duration of
        // gallery.scss.gallery-view__cards--dragging
        100 + 1
      )
    },
    /**
     * Is called when the user clicks on a row without moving it to another position.
     */
    rowClick() {},
  },
}
